import { combineReducers } from 'redux';
import * as types from '../actions/actionTypes';
import initialState from './initialState';
import { updateableReducer } from './common';
import { appStatuses, messageSeverities, updateStatuses, licenceStatuses } from '../statuses';
import imageCreationReducer from './imageCreationReducer';
import cloneCreationReducer from './cloneCreationReducer';
import appWorkflowReducer from './appWorkflowReducer';
import Notification from '../notification';
import Licence from '../models/Licence';
import { humanReadableListFrom } from '../utils';
import notificationTypes from '../notification/notificationTypes';
import notificationSeverities from '../notification/notificationSeverities';
import generateInstallerLink from '../serverApi/installerLinkGenerator';
import _ from 'underscore';

function appModeReducer(state = initialState.app.mode, action) {
    switch(action.type) {
        case types.APP_MODE_CHANGED:
            return action.mode;

        default:
            return state;
    }
}

function appStatusReducer(state = initialState.app.status, action) {
    switch(action.type) {
        case types.APP_INITIATED:
            return appStatuses.initiated;
        
        case types.APP_INITIATION_FAILED:
            return appStatuses.initiationFailed;

        case types.APP_DISCONNECTED:
            return appStatuses.disconnected;

        default:
            return state;
    }
}

function gettingStartedReducer(state = initialState.app.shouldShowGettingStarted, action) {
    switch(action.type) {
        case types.GETTING_STARTED_ENABLED_CHANGE_REQUESTED:
            return action.enable;

        default:
            return state;
    }
}

const licenceReducer = updateableReducer(actionType => {
    switch(actionType) {
        case types.APP_LICENCE_UPDATED:
            return (stateValue, actionValue) => new Licence(actionValue.state, actionValue.description);
        default:
            return null;
    }
}, initialState.app.licence);

function notificationReducer(state = initialState.app.notifications, action) {
    switch (action.type) {
        case types.APP_ERROR:
            return state.with(
                new Notification('An error occurred', 
                    action.error.userMessage + '\r\n' + action.error.technicalContext, notificationTypes.appError, 
                    notificationSeverities.error, action.time));
        case types.OPERATION_MESSAGE:
            if (action.value.severity === messageSeverities.error) {
                return state.with(
                    new Notification('An error occurred running an operation', 
                        action.value.message, notificationTypes.operationError, 
                        notificationSeverities.error, action.value.timestamp));
            }
            break;      

        case types.APP_UPDATE_AVAILABLE:
            const cfuMessage = action.value;
            return state.with(
                new Notification('Update available', cfuMessage.updateName, notificationTypes.update, 
                    notificationSeverities.info, cfuMessage.timestamp, [
                        { name: 'Details', externalLink: cfuMessage.detailsUrl },
                        { name: 'Download', externalLink: cfuMessage.downloadUrl }
                ]));

        case types.AGENTS_UPDATED:
            if (action.updateStatus !== updateStatuses.success) { break; }
            const machineNamesRequiringUpgrade = action.value.filter(a => a.status.requiresUpgrade).map(a => a.machineName);
            if (machineNamesRequiringUpgrade.length === 0) { break; }
            const readableList = humanReadableListFrom(machineNamesRequiringUpgrade);
            //This is pretty certain to end up beneath a CFU notification, but straight after upgrade, hopefully they'll be on the latest version so they'll see this
            return state.with(
                new Notification('Agent update required', 'Agent on ' + readableList + ' will not work until upgraded', 
                    notificationTypes.update, notificationSeverities.info, action.value.timestamp, [
                        { name: 'Download agent installer', externalLink: generateInstallerLink() }
                ]));

        case types.CLEAR_NOTIFICATION_REQUESTED:
            return state.without(action.notification);
        
        case types.APP_LICENCE_UPDATED:
            if (action.updateStatus !== updateStatuses.success) { break; }
            const clearedLicensingNotifications = state.withoutType(notificationTypes.licence);

            switch(action.value.state)
            {
                case licenceStatuses.licensed:
                    return clearedLicensingNotifications;

                case licenceStatuses.trial:
                    return clearedLicensingNotifications.with(
                        new Notification(action.value.description + '.', 'Once your trial ends, you won\'t be able to make new clones.', 
                            notificationTypes.licence, notificationSeverities.info, action.value.timestamp, [
                                { name: 'Buy SQL Clone', externalLink: 'https://www.red-gate.com/products/dba/sql-clone/' },
                                { name: 'Ask me later', dismiss: true }
                            ])
                    );
                case licenceStatuses.none:
                    return clearedLicensingNotifications.with(
                        new Notification('No SQL Clone licence', action.value.description, notificationTypes.licence, 
                            notificationSeverities.error, action.value.timestamp, [], false)
                    );
                case licenceStatuses.expired:
                    return clearedLicensingNotifications.with(
                        new Notification('Your trial has ended.', 'Please activate by running SQL Clone Server Setup from the server machine\'s Start Menu.', 
                            notificationTypes.licence, notificationSeverities.error, action.value.timestamp, [
                                { name: 'Buy SQL Clone', externalLink: 'https://www.red-gate.com/products/dba/sql-clone/' }
                            ], false)
                    );
            }
    }
    return state;
}

const baseAppReducer = combineReducers({
    status: appStatusReducer,
    licence: licenceReducer,
    notifications: notificationReducer,
    mode: appModeReducer,
    shouldShowGettingStarted: gettingStartedReducer,
    imageCreation: imageCreationReducer,
    cloneCreation: cloneCreationReducer
});

export default (state, action) => {
    return appWorkflowReducer(baseAppReducer(state, action), action);
};



// WEBPACK FOOTER //
// ./src/js/reducers/appReducer.js